import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import {
  Bookmark,
  FileText,
  Image,
  ImageIcon,
  Images,
  Mic,
  Music,
  Package,
  Sparkles,
  Upload,
  Video,
} from 'lucide-react'

import type { HandleDataType, StudioNodeType } from '@/features/studio/canvas/types'
import {
  libraryAssetDataType,
  type DockLibraryAsset,
} from '@/features/studio/canvas/canvas-dock-library'
import {
  CANVAS_NODE_CATALOG,
  CANVAS_NODE_CATEGORY_ICON,
  CANVAS_NODE_CATEGORY_LABEL_KEY,
  type CanvasNodeCatalogItem,
} from '@/features/studio/canvas/canvas-node-catalog'
import { Button } from '@/shared/components/ui/button'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'

const KIND_PLACEHOLDER_ICON: Record<HandleDataType, typeof Mic> = {
  image: Image,
  video: Video,
  audio: Mic,
  text: FileText,
  ctx: FileText,
}

interface DockItem extends CanvasNodeCatalogItem {}

const GROUPS = CANVAS_NODE_CATALOG
const CATEGORY_ICON = CANVAS_NODE_CATEGORY_ICON
const CATEGORY_LABEL_KEY = CANVAS_NODE_CATEGORY_LABEL_KEY

const QUICK_ITEMS: DockItem[] = [
  { type: 'text', icon: FileText, labelKey: 'canvas.node.text' },
  { type: 'upload', icon: Upload, labelKey: 'canvas.node.upload' },
  { type: 'gen_image', icon: Image, labelKey: 'canvas.node.genImage' },
  { type: 'gen_video', icon: Video, labelKey: 'canvas.node.genVideo' },
  { type: 'gen_audio', icon: Mic, labelKey: 'canvas.node.genAudio' },
  { type: 'register', icon: Package, labelKey: 'canvas.node.register' },
]

function LibraryThumb({
  asset,
}: {
  asset: DockLibraryAsset
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const dataType = libraryAssetDataType(asset)
  const PlaceholderIcon = KIND_PLACEHOLDER_ICON[dataType] ?? Image

  const displayUrl = resolveMediaDisplayUrl(asset.url) || undefined

  if (!displayUrl) {
    return <PlaceholderIcon className="h-4 w-4 text-slate-500" />
  }

  if (dataType === 'video') {
    return (
      <video
        src={displayUrl}
        className="h-full w-full object-cover pointer-events-none"
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
          const el = e.currentTarget
          if (el.currentTime === 0) el.currentTime = 0.01
        }}
      />
    )
  }

  if (dataType === 'audio') {
    return <Music className="h-4 w-4 text-pink-400" />
  }

  if (imgFailed) {
    return <PlaceholderIcon className="h-4 w-4 text-slate-500" />
  }

  return (
    <img
      src={displayUrl}
      alt=""
      className="h-full w-full object-cover"
      onError={() => setImgFailed(true)}
    />
  )
}

export function CanvasDock({
  expanded,
  onToggle,
  onAdd,
  libraryOpen,
  onLibraryToggle,
  promptsOpen,
  onPromptsToggle,
  libraryAssets,
  libraryLoading,
  placedAssetIds,
  manageHref,
  onPlaceAsset,
  onSetWorkspaceCover,
  hideNodeTypes,
}: {
  expanded: boolean
  onToggle: () => void
  onAdd: (type: StudioNodeType) => void
  libraryOpen: boolean
  onLibraryToggle: () => void
  promptsOpen: boolean
  onPromptsToggle: () => void
  libraryAssets: DockLibraryAsset[]
  libraryLoading?: boolean
  placedAssetIds: Set<string>
  manageHref: string
  onPlaceAsset: (asset: DockLibraryAsset) => void
  onSetWorkspaceCover?: (previewUrl: string) => void
  hideNodeTypes?: StudioNodeType[]
}) {
  const { t } = useTranslation()
  const assetCount = libraryAssets.length
  const hidden = new Set(hideNodeTypes ?? [])
  const quickItems = QUICK_ITEMS.filter((item) => !hidden.has(item.type))
  const groups = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !hidden.has(item.type)),
  })).filter((group) => group.items.length > 0)

  return (
    <div className="studio-canvas-dock">
      <div className="studio-canvas-dock__bar">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onToggle}
            title={t('canvas.dockAdd')}
            className={clsx('studio-canvas-dock__primary', expanded && 'studio-canvas-dock__primary--active')}
          >
            <Sparkles className="h-4 w-4 text-cyan-400" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onLibraryToggle}
            title={t('canvas.dockLibrary')}
            className={clsx(
              'studio-canvas-dock__icon relative',
              libraryOpen && 'studio-canvas-dock__icon--active',
            )}
          >
            <Images className="h-4 w-4" />
            {assetCount > 0 ? (
              <span className="studio-canvas-dock__badge">{assetCount}</span>
            ) : null}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onPromptsToggle}
            title={t('promptLibrary.title')}
            className={clsx(
              'studio-canvas-dock__icon',
              promptsOpen && 'studio-canvas-dock__icon--active',
            )}
          >
            <Bookmark className="h-4 w-4" />
          </Button>
          <div className="studio-canvas-dock__divider" />
          {quickItems.map(({ type, icon: Icon, labelKey }) => (
            <Button
              key={type}
              type="button"
              variant="ghost"
              size="icon-sm"
              title={t(labelKey)}
              onClick={() => onAdd(type)}
              className="studio-canvas-dock__icon"
            >
              <Icon className="h-4 w-4" />
            </Button>
          ))}
        </div>

      {libraryOpen ? (
          <div className="studio-canvas-dock__panel studio-canvas-dock__panel--history studio-scrollbar-hidden">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold tracking-wide text-slate-400">{t('canvas.dockLibrary')}</span>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={onLibraryToggle}
                className="h-auto shrink-0 px-0 text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
              >
                {t('canvas.dockCollapse')}
              </Button>
            </div>
            <p className="studio-canvas-dock__history-hint">{t('canvas.dockLibraryHint')}</p>
            <Link
              to={manageHref}
              className="mb-2 inline-flex text-[11px] text-violet-300 underline-offset-2 hover:underline"
            >
              {t('canvas.dockLibraryManage')}
            </Link>
            {libraryLoading ? (
              <p className="studio-canvas-dock__history-empty">{t('common.loading')}</p>
            ) : libraryAssets.length === 0 ? (
              <p className="studio-canvas-dock__history-empty">{t('canvas.dockLibraryEmpty')}</p>
            ) : (
              <ul className="studio-canvas-dock__history-list">
                {libraryAssets.map((asset) => {
                  const onCanvas = placedAssetIds.has(asset.publicId) || placedAssetIds.has(asset.id)
                  const canSetCover = Boolean(onSetWorkspaceCover && asset.url.trim())
                  return (
                    <li
                      key={asset.publicId || asset.id}
                      className={clsx(
                        'studio-canvas-dock__history-row',
                        onCanvas && 'studio-canvas-dock__history-row--active',
                      )}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onPlaceAsset(asset)}
                        className="studio-canvas-dock__history-item h-auto min-w-0 flex-1 justify-start"
                      >
                        <span className="studio-canvas-dock__history-thumb">
                          <LibraryThumb asset={asset} />
                        </span>
                        <span className="studio-canvas-dock__history-meta">
                          <span className="studio-canvas-dock__history-node-label truncate">{asset.name}</span>
                          <span className="studio-canvas-dock__history-status">
                            {onCanvas ? t('canvas.dockLibraryOnCanvas') : t('canvas.dockLibraryPlace')}
                          </span>
                        </span>
                      </Button>
                      <div className="studio-canvas-dock__history-actions">
                        {canSetCover ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            title={t('canvas.historySetCover')}
                            aria-label={t('canvas.historySetCover')}
                            className="studio-canvas-dock__history-action"
                            onClick={(e) => {
                              e.stopPropagation()
                              onSetWorkspaceCover?.(asset.url.trim())
                            }}
                          >
                            <ImageIcon className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ) : null}

      {expanded ? (
          <div className="studio-canvas-dock__panel studio-scrollbar-hidden">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold tracking-wide text-slate-400">{t('canvas.dockTitle')}</span>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={onToggle}
                className="h-auto shrink-0 px-0 text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
              >
                {t('canvas.dockCollapse')}
              </Button>
            </div>
            <div className="studio-canvas-dock__sections">
              {groups.map((group) => {
                const CatIcon = CATEGORY_ICON[group.category]
                return (
                  <div key={group.category} className="studio-canvas-dock__section">
                    <div className="studio-canvas-dock__group-label">
                      <CatIcon className="h-3 w-3 text-indigo-400/80" />
                      {t(CATEGORY_LABEL_KEY[group.category])}
                    </div>
                    <div className="studio-canvas-dock__list">
                      {group.items.map(({ type, icon: Icon, labelKey }) => (
                        <Button
                          key={type}
                          type="button"
                          variant="ghost"
                          onClick={() => onAdd(type)}
                          className="studio-canvas-dock__tile h-auto w-full justify-start"
                        >
                          <span className="studio-canvas-dock__tile-icon">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="truncate">{t(labelKey)}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
    </div>
  )
}
